// src/pages/admin/UserDetailPage.tsx
// ============================================================
// Summit League v2 — Admin User Detail Page
//
// Full user profile view for admins. Shows:
//   - User info card (identity, socials, status)
//   - Active teams list
//   - Recent matches
//   - Admin action history log
//   - Action buttons: Ban/Suspend, Unban, Change Role
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import UserStatusBadge from '../../components/admin/UserStatusBadge';
import UserRoleBadge from '../../components/admin/UserRoleBadge';
import BanUserModal from '../../components/admin/BanUserModal';
import ChangeRoleModal from '../../components/admin/ChangeRoleModal';
import Button from '../../components/ui/Button';

import '../../styles/admin-users.css';

interface UserTeam {
  team_id: number;
  team_name: string;
  team_tag: string;
  game_name: string;
  game_id: number;
  season_name: string;
  role: string;
  joined_at: string;
}

interface UserMatch {
  match_id: number;
  match_type: string;
  match_format: string;
  match_state: string;
  team_a_name: string;
  team_b_name: string | null;
  team_a_score: number;
  team_b_score: number;
  created_at: string;
  completed_at: string | null;
}

interface AdminAction {
  admin_action_id: number;
  admin_username: string;
  action_type: string;
  action_data: Record<string, unknown>;
  reason: string | null;
  created_at: string;
}

interface UserDetail {
  user_id: number;
  username: string;
  email: string;
  email_verified: boolean;
  role: string;
  avatar_url: string | null;
  bio: string | null;
  discord_handle: string | null;
  twitch_handle: string | null;
  twitter_handle: string | null;
  youtube_handle: string | null;
  steam_handle: string | null;
  status: string;
  ban_reason: string | null;
  ban_expires_at: string | null;
  banned_at: string | null;
  banned_by_username: string | null;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  teams: UserTeam[];
  recentMatches: UserMatch[];
  adminActions: AdminAction[];
}

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modals
  const [showBanModal, setShowBanModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionSuccess, setActionSuccess] = useState('');

  const fetchUser = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosClient.get(`/admin/users/${userId}`);
      setUser(res.data.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Clear success message after 4s
  useEffect(() => {
    if (!actionSuccess) return;
    const t = setTimeout(() => setActionSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [actionSuccess]);

  const handleBan = async (data: { type: 'ban' | 'suspend'; reason: string; duration?: number }) => {
    setIsSubmitting(true);
    try {
      const res = await axiosClient.post(`/admin/users/${userId}/ban`, data);
      const result = res.data.data;
      setShowBanModal(false);
      setActionSuccess(
        `User ${result.status}. ${
          result.teamsAffected.length > 0
            ? `${result.teamsAffected.length} team(s) affected.`
            : ''
        }`
      );
      await fetchUser();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Ban failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnban = async () => {
    const reason = window.prompt('Reason for unbanning:');
    if (!reason || reason.trim().length < 3) return;

    setIsSubmitting(true);
    try {
      await axiosClient.post(`/admin/users/${userId}/unban`, { reason: reason.trim() });
      setActionSuccess('User unbanned successfully.');
      await fetchUser();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unban failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoleChange = async (data: { role: string; reason: string }) => {
    setIsSubmitting(true);
    try {
      await axiosClient.post(`/admin/users/${userId}/role`, data);
      setShowRoleModal(false);
      setActionSuccess(`Role updated to ${data.role}.`);
      await fetchUser();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Role change failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatActionType = (type: string) => {
    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (loading) {
    return (
      <div className="sl-admin-users">
        <div className="sl-admin-loading">Loading user...</div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="sl-admin-users">
        <div className="sl-admin-error">{error}</div>
        <Button variant="ghost" onClick={() => navigate('/admin/users')}>
          Back to Users
        </Button>
      </div>
    );
  }

  if (!user) return null;

  const isBannedOrSuspended = user.status === 'banned' || user.status === 'suspended';
  const isDeleted = user.status === 'deleted';
  const socials = [
    user.discord_handle && { label: 'Discord', value: user.discord_handle },
    user.twitch_handle && { label: 'Twitch', value: user.twitch_handle },
    user.twitter_handle && { label: 'Twitter', value: user.twitter_handle },
    user.youtube_handle && { label: 'YouTube', value: user.youtube_handle },
    user.steam_handle && { label: 'Steam', value: user.steam_handle },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <div className="sl-admin-users">
      {/* Header */}
      <div className="sl-user-detail-header">
        <button className="sl-user-back-btn" onClick={() => navigate('/admin/users')}>
          ← Users
        </button>
        <div className="sl-user-detail-title-row">
          <h1 className="sl-admin-page-title">{user.username}</h1>
          <UserRoleBadge role={user.role} />
          <UserStatusBadge status={user.status} />
        </div>
      </div>

      {/* Success banner */}
      {actionSuccess && (
        <div className="sl-user-success-banner">{actionSuccess}</div>
      )}

      {/* Error banner */}
      {error && <div className="sl-admin-error">{error}</div>}

      {/* Action Bar */}
      <div className="sl-user-actions-bar">
        {!isBannedOrSuspended && !isDeleted && user.role !== 'admin' && (
          <button
            className="sl-user-action-btn sl-user-action-btn--danger"
            onClick={() => setShowBanModal(true)}
            disabled={isSubmitting}
          >
            Ban / Suspend
          </button>
        )}
        {isBannedOrSuspended && (
          <button
            className="sl-user-action-btn sl-user-action-btn--success"
            onClick={handleUnban}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Unban / Unsuspend'}
          </button>
        )}
        {!isDeleted && (
          <button
            className="sl-user-action-btn"
            onClick={() => setShowRoleModal(true)}
            disabled={isSubmitting}
          >
            Change Role
          </button>
        )}
      </div>

      <div className="sl-user-detail-grid">
        {/* User Info Card */}
        <div className="sl-admin-card sl-user-info-card">
          <h2 className="sl-admin-card-title">User Information</h2>
          <div className="sl-user-info-body">
            <div className="sl-user-avatar-lg">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="sl-user-avatar-img" />
              ) : (
                <span className="sl-user-avatar-fallback sl-user-avatar-fallback--lg">
                  {user.username.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="sl-user-info-rows">
              <div className="sl-user-info-row">
                <span className="sl-user-info-label">Email</span>
                <span className="sl-user-info-value">
                  {user.email}
                  {user.email_verified && <span className="sl-user-verified" title="Verified">✓</span>}
                </span>
              </div>
              <div className="sl-user-info-row">
                <span className="sl-user-info-label">User ID</span>
                <span className="sl-user-info-value sl-user-info-mono">{user.user_id}</span>
              </div>
              <div className="sl-user-info-row">
                <span className="sl-user-info-label">Joined</span>
                <span className="sl-user-info-value">{formatDate(user.created_at)}</span>
              </div>
              <div className="sl-user-info-row">
                <span className="sl-user-info-label">Last Login</span>
                <span className="sl-user-info-value">{formatDate(user.last_login_at)}</span>
              </div>
              {user.bio && (
                <div className="sl-user-info-row">
                  <span className="sl-user-info-label">Bio</span>
                  <span className="sl-user-info-value">{user.bio}</span>
                </div>
              )}
            </div>
          </div>

          {/* Socials */}
          {socials.length > 0 && (
            <div className="sl-user-socials">
              {socials.map((s) => (
                <div key={s.label} className="sl-user-social-item">
                  <span className="sl-user-social-label">{s.label}</span>
                  <span className="sl-user-social-value">{s.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ban Info Card (only if banned/suspended) */}
        {isBannedOrSuspended && (
          <div className="sl-admin-card sl-user-ban-card">
            <h2 className="sl-admin-card-title">
              {user.status === 'banned' ? '🚫 Ban' : '⏸️ Suspension'} Details
            </h2>
            <div className="sl-user-info-rows">
              <div className="sl-user-info-row">
                <span className="sl-user-info-label">Reason</span>
                <span className="sl-user-info-value">{user.ban_reason || '—'}</span>
              </div>
              <div className="sl-user-info-row">
                <span className="sl-user-info-label">Issued At</span>
                <span className="sl-user-info-value">{formatDate(user.banned_at)}</span>
              </div>
              <div className="sl-user-info-row">
                <span className="sl-user-info-label">Expires</span>
                <span className="sl-user-info-value">
                  {user.ban_expires_at ? formatDate(user.ban_expires_at) : 'Permanent'}
                </span>
              </div>
              <div className="sl-user-info-row">
                <span className="sl-user-info-label">Issued By</span>
                <span className="sl-user-info-value">{user.banned_by_username || '—'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Teams Card */}
        <div className="sl-admin-card">
          <h2 className="sl-admin-card-title">
            Teams <span className="sl-admin-card-count">{user.teams.length}</span>
          </h2>
          {user.teams.length === 0 ? (
            <div className="sl-admin-empty">No active teams</div>
          ) : (
            <div className="sl-user-teams-list">
              {user.teams.map((team) => (
                <div key={team.team_id} className="sl-user-team-row">
                  <div className="sl-user-team-info">
                    <span className="sl-user-team-name">{team.team_name}</span>
                    <span className="sl-user-team-tag">[{team.team_tag}]</span>
                    <span className={`sl-user-team-role ${team.role === 'captain' ? 'sl-user-team-role--captain' : ''}`}>
                      {team.role}
                    </span>
                  </div>
                  <div className="sl-user-team-meta">
                    <span>{team.game_name}</span>
                    {team.season_name && <span>• {team.season_name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Matches Card */}
        <div className="sl-admin-card">
          <h2 className="sl-admin-card-title">
            Recent Matches <span className="sl-admin-card-count">{user.recentMatches.length}</span>
          </h2>
          {user.recentMatches.length === 0 ? (
            <div className="sl-admin-empty">No recent matches</div>
          ) : (
            <div className="sl-user-matches-list">
              {user.recentMatches.map((match) => (
                <div key={match.match_id} className="sl-user-match-row">
                  <div className="sl-user-match-teams">
                    <span>{match.team_a_name}</span>
                    <span className="sl-user-match-score">
                      {match.team_a_score} - {match.team_b_score}
                    </span>
                    <span>{match.team_b_name || 'TBD'}</span>
                  </div>
                  <div className="sl-user-match-meta">
                    <span className="sl-user-match-format">{match.match_format}</span>
                    <span className="sl-user-match-state">{match.match_state}</span>
                    <span className="sl-user-match-date">{formatDate(match.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Admin Action Log */}
        <div className="sl-admin-card">
          <h2 className="sl-admin-card-title">
            Admin Action Log <span className="sl-admin-card-count">{user.adminActions.length}</span>
          </h2>
          {user.adminActions.length === 0 ? (
            <div className="sl-admin-empty">No admin actions recorded</div>
          ) : (
            <div className="sl-user-actions-log">
              {user.adminActions.map((action) => (
                <div key={action.admin_action_id} className="sl-user-action-entry">
                  <div className="sl-user-action-header">
                    <span className="sl-user-action-type">
                      {formatActionType(action.action_type)}
                    </span>
                    <span className="sl-user-action-date">
                      {formatDate(action.created_at)}
                    </span>
                  </div>
                  <div className="sl-user-action-by">by {action.admin_username}</div>
                  {action.reason && (
                    <div className="sl-user-action-reason">{action.reason}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ban Modal */}
      <BanUserModal
        isOpen={showBanModal}
        username={user.username}
        onClose={() => setShowBanModal(false)}
        onSubmit={handleBan}
        isSubmitting={isSubmitting}
      />

      {/* Change Role Modal */}
      <ChangeRoleModal
        isOpen={showRoleModal}
        username={user.username}
        currentRole={user.role}
        onClose={() => setShowRoleModal(false)}
        onSubmit={handleRoleChange}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
